/**
 * Admin / super-admin sidebar.
 *
 * A full labelled sidebar (brand → grouped sections with headers → user footer)
 * that collapses to a mini icon-only rail with tooltips. Rendered only for the
 * admin / super_admin roles; every other role keeps <MinimalSidebarV2>.
 *
 * `mini` + `onToggleMini` are controlled by the layout so it can offset the page
 * content (sm:ml-64 expanded / sm:ml-20 mini).
 */
import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronsLeftIcon, ChevronsRightIcon, LogOutIcon } from "lucide-react";

import { NavItem } from "@type/navItems";
import { useUserProfileContext } from "@context/user_profile";
import { removeCookie } from "@util/cookie";
import { getPostAuthLandingPath } from "@util/auth-redirect";
import { cn } from "@util/tailwind";
import { Tooltip } from "./tooltip";

interface Props {
    navItems: NavItem[];
    mini: boolean;
    onToggleMini: () => void;
}

// Section-header labels for the `group` keys used by the admin nav.
const GROUP_LABELS: Record<string, string> = {
    recruitment: "Recruitment",
    billing: "Billing",
    administration: "Administration",
    tools: "Tools",
    // Oura, the local-business AI-visibility product. Its own section rather
    // than a slot under Tools: it is a product with its own surfaces, not an
    // admin utility, and listing it beside Scheduler Test would say otherwise.
    growth: "OURA",
};

// Sections that lead the sidebar, in this order, before the rest (which keep the
// nav's own order). Oura is the product staff open first (#4574).
const LEADING_GROUPS = ["growth"];

const ROLE_LABELS: Record<string, string> = {
    super_admin: "Super Admin",
    admin: "Admin",
};

export default function AdminSidebar({ navItems, mini, onToggleMini }: Props) {
    const navigate = useNavigate();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const role = currentUserInfo?.role || "";
    const currentPath = window.location.pathname;
    /**
     * Which nav item is lit.
     *
     * Exact match first, then the longest item whose path the URL sits under —
     * so a drill-in keeps its section lit instead of going dark. Every nested
     * route had this: `/candidate-investigator/:id` and `/training/aiqm/:id`
     * both unlit their own menu entry, and Oura made it constant, since opening
     * any business leaves `/oura/businesses` for `/oura/businesses/:id`.
     *
     * The `/` guard is what keeps the prefix honest: without it `/jobs` would
     * light for `/jobs-archive`, which is a different page. Longest-wins means
     * a future `/oura/businesses/reports` item beats the parent rather than
     * lighting both.
     */
    const currentPage = (
        navItems.find((item) => item.to === currentPath)
        ?? navItems
            .filter((item) => typeof item.to === "string" && currentPath.startsWith(`${item.to}/`))
            .sort((a, b) => String(b.to).length - String(a.to).length)[0]
    )?.name;

    const handleLogout = () => {
        removeCookie("__apply_job_guest__");
        removeCookie("authToken");
        removeCookie("__account_startup_screening__");
        sessionStorage.removeItem("cv_uuid");
        sessionStorage.removeItem("__INVITE_MEMBER_EMAIL__");
        window.location.href = "/";
    };

    // Role-filtered items, grouped by `group` in first-appearance order.
    const visibleItems = (navItems || []).filter((item) => item.roles.indexOf(role) !== -1);
    const groups: { key: string; items: NavItem[] }[] = [];
    for (const item of visibleItems) {
        const key = item.group || "__default__";
        let group = groups.find((g) => g.key === key);
        if (!group) {
            group = { key, items: [] };
            groups.push(group);
        }
        group.items.push(item);
    }
    // Leading sections first; Array.sort is stable, so the others keep their order.
    const lead = (key: string) => {
        const at = LEADING_GROUPS.indexOf(key);
        return at === -1 ? LEADING_GROUPS.length : at;
    };
    groups.sort((a, b) => lead(a.key) - lead(b.key));

    const displayName =
        [currentUserInfo?.first_name, currentUserInfo?.last_name].filter(Boolean).join(" ") ||
        currentUserInfo?.username ||
        currentUserInfo?.email ||
        "";
    const avatarSrc = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${currentUserInfo?.company_logo}`;
    const avatarFallback = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/super-admin-logo.png`;

    const Avatar = (
        <img
            className="h-9 w-9 flex-shrink-0 rounded-full border border-gray-200 object-cover bg-white"
            alt={role}
            src={avatarSrc}
            onError={(e) => {
                e.currentTarget.src = avatarFallback;
            }}
        />
    );

    return (
        <div
            className={cn(
                "fixed left-0 top-0 z-10 hidden h-screen flex-col border-r border-gray-200 bg-white transition-[width] duration-200 sm:flex dark:bg-gray-900 dark:border-gray-800",
                mini ? "w-20" : "w-64",
            )}
        >
            {/* Header: brand + collapse toggle */}
            <div
                className={cn(
                    "flex items-center border-b border-gray-200 dark:border-gray-800",
                    mini ? "flex-col gap-3 py-4" : "justify-between px-4 py-4",
                )}
            >
                <Link to={getPostAuthLandingPath(role)} className="flex items-center gap-2">
                    <img
                        width={mini ? 36 : 40}
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_dashboard.png`}
                        alt="ourteam"
                    />
                    {!mini && <span className="text-lg font-semibold text-gray-900">ourteam</span>}
                </Link>
                <Tooltip content={mini ? "Expand menu" : "Collapse menu"} side="right">
                    <button
                        onClick={onToggleMini}
                        aria-label={mini ? "Expand menu" : "Collapse menu"}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                        {mini ? <ChevronsRightIcon className="h-4 w-4" /> : <ChevronsLeftIcon className="h-4 w-4" />}
                    </button>
                </Tooltip>
            </div>

            {/* Nav */}
            {!isLoadingUserInfo && currentUserInfo && (
                mini ? (
                    <nav className="flex w-full flex-1 flex-col items-center gap-4 overflow-y-auto scrollbar-hide py-4">
                        {groups.map((group, gi) => (
                            <Fragment key={group.key}>
                                {gi > 0 && <div className="w-8 border-t border-gray-200 dark:border-gray-800" />}
                                {group.items.map((item) => {
                                    const isActive = currentPage === item.name;
                                    const Icon = item.icon;
                                    return (
                                        <Tooltip key={item.name} content={item.tooltip || item.label} side="right" asChild>
                                            <button
                                                onClick={() => navigate(item.to as string)}
                                                className="flex items-center justify-center"
                                            >
                                                <div
                                                    className={cn(
                                                        "rounded-lg p-2 transition-colors",
                                                        isActive
                                                            ? "bg-blue-50 text-blue-600 dark:bg-gray-800 dark:text-blue-400"
                                                            : "text-gray-500 hover:bg-blue-50 hover:text-blue-600",
                                                    )}
                                                >
                                                    <Icon className="h-6 w-6 shrink-0" />
                                                </div>
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </nav>
                ) : (
                    <nav className="flex-1 overflow-y-auto px-3 py-3">
                        {groups.map((group) => (
                            <div key={group.key} className="mb-3">
                                {GROUP_LABELS[group.key] && (
                                    <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                        {GROUP_LABELS[group.key]}
                                    </p>
                                )}
                                <div className="space-y-0.5">
                                    {group.items.map((item) => {
                                        const isActive = currentPage === item.name;
                                        const Icon = item.icon;
                                        return (
                                            <button
                                                key={item.name}
                                                onClick={() => navigate(item.to as string)}
                                                className={cn(
                                                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                                                    isActive
                                                        ? "bg-blue-50 font-medium text-blue-700 dark:bg-gray-800 dark:text-blue-400"
                                                        : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800",
                                                )}
                                            >
                                                <Icon className={cn("h-5 w-5 flex-shrink-0", isActive ? "text-blue-600" : "")} />
                                                <span className="truncate text-left">{item.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                )
            )}

            {/* User footer */}
            <div className="mt-auto border-t border-gray-200 dark:border-gray-800">
                {mini ? (
                    <div className="flex flex-col items-center gap-2 py-3">
                        <Tooltip content={displayName} side="right">
                            <button onClick={() => navigate("/setting")} aria-label="Settings">
                                {Avatar}
                            </button>
                        </Tooltip>
                        <Tooltip content="Logout" side="right">
                            <button
                                onClick={handleLogout}
                                aria-label="Logout"
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                            >
                                <LogOutIcon className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </div>
                ) : (
                    <div className="flex items-center gap-3 p-3">
                        <button
                            onClick={() => navigate("/setting")}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        >
                            {Avatar}
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
                                <p className="truncate text-xs text-gray-500">
                                    {ROLE_LABELS[role] || role}
                                </p>
                            </div>
                        </button>
                        <Tooltip content="Logout" side="top">
                            <button
                                onClick={handleLogout}
                                aria-label="Logout"
                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-rose-50 hover:text-rose-600"
                            >
                                <LogOutIcon className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
        </div>
    );
}
